/**
 * PM-WF-01 — bulk "Set workflow…" on the Projects list selection.
 *
 * One dialog, one picker (M3C-04: the tenant's own active VERSIONS, by id),
 * one confirmation listing the projects → ONE
 * `PATCH /projects/{id}/workflow {workflowVersionId}` PER PROJECT, sent one after another. The API
 * has no bulk endpoint and no cross-project transaction, by decision (story
 * §4): each project gets its own audited write, and a refusal on one project
 * (e.g. 404, 422) is shown on that project while the others still apply.
 *
 * At most {@link MAX_BULK_WORKFLOW} projects per action. Progress is shown
 * ("k of n"); when the run ends each project carries its result.
 */
import { useEffect, useState, type ReactElement } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { projectsKeys } from "./keys.js";
import { patchProjectWorkflow } from "./useProjects.js";
import {
  WorkflowSelect,
  projectWorkflowLabel,
  workflowErrorMessage,
} from "./WorkflowSelect.js";
import { useWorkflows } from "../workflows/api/workflows.js";
import { NEW_ACQUISITIONS_ONLY_NOTE } from "./detail/WorkflowPanel.js";

export const MAX_BULK_WORKFLOW = 50;

export interface BulkWorkflowProject {
  projectId: string;
  projectName: string;
}

type RowResult =
  | { state: "pending" }
  | { state: "ok" }
  | { state: "error"; message: string };

export interface BulkSetWorkflowDialogProps {
  open: boolean;
  onClose: () => void;
  projects: readonly BulkWorkflowProject[];
  /** Fired after a run finished (so the page can clear its selection). */
  onFinished?: (summary: { ok: number; failed: number }) => void;
}

export function BulkSetWorkflowDialog({
  open,
  onClose,
  projects: selected,
  onFinished,
}: BulkSetWorkflowDialogProps): ReactElement {
  // The selection is snapshotted when the dialog opens: the list refreshes
  // after the run (and under the "not set" filter the updated projects leave
  // it), but the dialog keeps showing every project with its result.
  const [projects, setProjects] =
    useState<readonly BulkWorkflowProject[]>(selected);
  useEffect(() => {
    if (open) setProjects(selected);
    // Only on open: later changes of the page's selection must not reshape a
    // run in progress or its results.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const qc = useQueryClient();
  const workflows = useWorkflows();
  const [workflow, setWorkflow] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [running, setRunning] = useState(false);
  const [done, setDone] = useState(0);
  const [results, setResults] = useState<Record<string, RowResult>>({});
  const finished =
    !running &&
    projects.length > 0 &&
    Object.keys(results).length === projects.length;

  const tooMany = projects.length > MAX_BULK_WORKFLOW;

  function reset(): void {
    setWorkflow("");
    setConfirming(false);
    setRunning(false);
    setDone(0);
    setResults({});
  }
  function close(): void {
    if (running) return;
    reset();
    onClose();
  }

  async function run(): Promise<void> {
    setRunning(true);
    setDone(0);
    const initial: Record<string, RowResult> = {};
    for (const p of projects) initial[p.projectId] = { state: "pending" };
    setResults(initial);
    let ok = 0;
    let failed = 0;
    // Sequential on purpose: one audited PATCH per project, no burst.
    for (const p of projects) {
      try {
        await patchProjectWorkflow(p.projectId, workflow);
        ok += 1;
        setResults((prev) => ({ ...prev, [p.projectId]: { state: "ok" } }));
      } catch (err) {
        failed += 1;
        setResults((prev) => ({
          ...prev,
          [p.projectId]: { state: "error", message: workflowErrorMessage(err) },
        }));
      }
      setDone((d) => d + 1);
    }
    setRunning(false);
    void qc.invalidateQueries({ queryKey: projectsKeys.all });
    onFinished?.({ ok, failed });
  }

  const label = workflow ? projectWorkflowLabel(workflow, workflows.data) : "";
  const okCount = Object.values(results).filter((r) => r.state === "ok").length;
  const failedCount = Object.values(results).filter(
    (r) => r.state === "error",
  ).length;

  return (
    <Dialog
      open={open}
      onClose={close}
      fullWidth
      maxWidth="sm"
      aria-labelledby="bulk-workflow-title"
    >
      <DialogTitle id="bulk-workflow-title">
        Set workflow for {String(projects.length)} project
        {projects.length === 1 ? "" : "s"}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {tooMany ? (
            <Alert severity="warning">
              At most {String(MAX_BULK_WORKFLOW)} projects per action — narrow
              the selection.
            </Alert>
          ) : null}

          {!confirming && !running && !finished ? (
            <>
              <WorkflowSelect
                value={workflow}
                onChange={setWorkflow}
                required
                id="bulk-project-workflow"
              />
              <Typography variant="body2" color="text.secondary">
                {NEW_ACQUISITIONS_ONLY_NOTE}
              </Typography>
            </>
          ) : null}

          {confirming || running || finished ? (
            <>
              {!running && !finished ? (
                <Typography variant="body2">
                  Set <strong>{label}</strong> on these projects?{" "}
                  {NEW_ACQUISITIONS_ONLY_NOTE}
                </Typography>
              ) : null}
              {running || finished ? (
                <Stack spacing={0.5}>
                  <Typography variant="body2" aria-live="polite">
                    {running
                      ? `Updating ${String(done)} of ${String(projects.length)}…`
                      : `Done: ${String(okCount)} updated${failedCount > 0 ? `, ${String(failedCount)} failed` : ""}.`}
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={
                      projects.length === 0 ? 0 : (done / projects.length) * 100
                    }
                    aria-label="Bulk workflow progress"
                  />
                </Stack>
              ) : null}
              <List
                dense
                aria-label="Projects to update"
                sx={{ maxHeight: 280, overflowY: "auto" }}
              >
                {projects.map((p) => {
                  const r = results[p.projectId];
                  const secondary =
                    r === undefined
                      ? null
                      : r.state === "ok"
                        ? "Updated"
                        : r.state === "error"
                          ? r.message
                          : "Waiting…";
                  return (
                    <ListItem
                      key={p.projectId}
                      disableGutters
                      data-testid={`bulk-row-${p.projectId}`}
                    >
                      <ListItemText
                        primary={p.projectName}
                        secondary={secondary}
                        {...(r?.state === "error"
                          ? { secondaryTypographyProps: { color: "error" } }
                          : {})}
                      />
                    </ListItem>
                  );
                })}
              </List>
            </>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        {finished ? (
          <Button variant="contained" onClick={close}>
            Close
          </Button>
        ) : confirming ? (
          <>
            <Button
              onClick={() => {
                setConfirming(false);
              }}
              disabled={running}
            >
              Back
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                void run();
              }}
              disabled={running || tooMany || !workflow}
            >
              {running
                ? "Updating…"
                : `Set workflow on ${String(projects.length)}`}
            </Button>
          </>
        ) : (
          <>
            <Button onClick={close}>Cancel</Button>
            <Button
              variant="contained"
              onClick={() => {
                setConfirming(true);
              }}
              disabled={!workflow || tooMany || projects.length === 0}
            >
              Continue
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
